import React, { useContext, memo, useState, useEffect, useRef } from "react";
import { Marker, Polygon, Polyline } from "@react-google-maps/api";
import {
  fileTypes,
  polygonLineTypes,
  SiteUploadIcons,
} from "../../../../constants/WebMap";
import { WebMapContext } from "../WebMapGoogleMap";
import { getUploadedDataFromFile } from "../../../../services/api/mapFileUpload.api";
import ReactDOMServer from "react-dom/server";
import { BackDrop } from "../../../../components/elements/Loader";
import { getDashedOutline } from "../../../../helpers/webMap";
import { toast } from "react-toastify";

const UploadedFilesSection = () => {
  const { appliedFilters, setSelectedUploadedFile, selectedUploadedFile } = useContext(WebMapContext);
  const prevSelectedFileRef = useRef([]);

  const [sites, setSites] = useState([
    { fileName: "", color: "", coordinates: [] },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      appliedFilters.country ||
      (appliedFilters.year &&
      appliedFilters.quarter)
    ) {
      setSelectedUploadedFile();
    }
  }, [
    appliedFilters.country,
    appliedFilters.year,
    appliedFilters.quarter,
    setSelectedUploadedFile,
  ]);

  useEffect(() => {
    const prevSelectedFile = prevSelectedFileRef.current;

    // Detect removed files
    const removedFiles = prevSelectedFile?.filter(
      (prevFile) =>
        !selectedUploadedFile?.some((file) => file.file_name === prevFile.file_name)
    );

    if (removedFiles?.length > 0) {
      // Update the `sites` variable to remove the corresponding entries
      setSites((prevSites) =>
        prevSites.filter(
          (site) =>
            site.fileName &&
            !removedFiles?.some((removed) => removed.file_name === site.fileName)
        )
      );
    }

    // Detect added files
    const newFiles = selectedUploadedFile?.filter(
      (file) =>
        !prevSelectedFile?.some(
          (prevFile) => prevFile.file_name === file.file_name
        )
    );

    if (newFiles?.length > 0) {
      // Fetch for new files only
      newFiles.forEach(async (newFile) => {
        await fetchUploadedSites(newFile);
      });
    }

    // Update the reference to the current `selectedFile`
    prevSelectedFileRef.current = selectedUploadedFile;
  }, [selectedUploadedFile, sites]);

  const fetchUploadedSites = async (newFile) => {
    try {
      setIsLoading(true);
      const siteData = await getUploadedDataFromFile(newFile?._id);

      setSites((prevSites) => [
        ...prevSites,
        {
          fileName: newFile.file_name,
          icon: newFile?.icon,
          type: newFile?.type,
          color: newFile.color,
          coordinates: siteData?.coordinates,
        },
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.error, {
        theme: "colored",
      });
      console.error("Error fetching sites:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? <BackDrop /> : null}

      {sites?.map((item) =>
        item.type === fileTypes.POLYGON
          ? item?.coordinates?.map((polygon, id) => {
              const color = item?.color;

              const outLine = getDashedOutline(color);

              // Convert polygon coordinates to Google Maps format
              const path = polygon?.coordinates?.map(
                ({ latitude, longitude }) => ({
                  lat: latitude,
                  lng: longitude,
                })
              );

              return (
                <>
                  {/* Render the main filled Polygon */}
                  <Polygon
                    key={`polygon-${sites?.fileName}-${polygon?.name}${id}`}
                    path={path}
                    options={{
                      fillColor: color,
                      fillOpacity: 0.4,
                      strokeColor: color,
                      strokeOpacity: 0.7,
                      strokeWeight: 0,
                      clickable: false,
                      draggable: false,
                      editable: false,
                      geodesic: false,
                      zIndex: 1,
                    }}
                    
                  />

                  {/* Render the dashed border using a Polyline */}
                  <Polyline
                    key={`polyline-${sites?.fileName}-${polygon?.name}${id}`}
                    path={path}
                    options={{
                      strokeColor: color,
                      strokeOpacity: 0.9,
                      strokeDasharray: 8,
                      strokeWeight:
                        item.icon === polygonLineTypes.POLYGON_LINE ? 4 : 0,
                      icons: outLine[item.icon],
                      clickable: false,
                      draggable: false,
                      editable: false,
                      geodesic: false,
                      zIndex: 2,
                    }}
                  />
                </>
              );
            })
          : sites?.map((item) =>
              item?.coordinates?.map((site, idx) => (
                <Marker
                  key={item?.fileName +"-"+idx}
                  position={{ lat: site.latitude, lng: site.longitude }}
                  onLoad={(marker) => {
                    const SelectedIcon =
                      SiteUploadIcons[item?.icon] || SiteUploadIcons.pin;

                    // Convert the selected SVG component to a static string
                    const svgString = ReactDOMServer.renderToStaticMarkup(
                      React.cloneElement(SelectedIcon, {
                        fill: item?.color,
                        stroke: "white",
                      })
                    );

                    // Encode the SVG string as a data URL
                    const svgUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                      svgString
                    )}`;

                    const customIcon = {
                      url: svgUrl,
                      animation: window.google.maps.Animation.DROP,
                      fillColor: item?.color,
                      strokeColor: "white",
                      anchor: new window.google.maps.Point(14, 28),
                      scaledSize: new window.google.maps.Size(28, 56),
                    };
                    marker.setIcon(customIcon);
                  }}
                />
              ))
            )
      )}
    </>
  );
};

export default memo(UploadedFilesSection);
